import { useState } from 'react'
import { Outlet, createSearchParams, useSearchParams } from 'react-router-dom'

import { Card, Grid, Icon } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import type { NewRequestPageQuery } from 'generated/resources/graphql'
import { NewRequestPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { InfiniteLoader, usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import WithPaywall from 'modules/procurement/components/WithPaywall'
import { PurchaseRequestForm } from 'modules/procurement/components/purchaseRequests/PurchaseRequestForm'

type ProcurementType =
  NewRequestPageQuery['procurement']['typesConnection']['edges'][number]['node']

const NewRequestPage = () => {
  const { navigation } = useNavigation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedType, setSelectedType] = useState<ProcurementType | null>(null)

  const { company } = useCurrentSession()

  const { data, error, loading, nodes, controls } = usePaginatedQuery(NewRequestPageDocument, {
    variables: { companyId: company.id, enabled: true },
    extractConnection: (queryData) => queryData.procurement.typesConnection,
  })

  const requestTypes = nodes

  const isFormOpen = searchParams.get('modal') === 'new'

  const getIcon = (type: ProcurementType['identifier']) => {
    switch (type) {
      case 'software':
        return 'floppy-disk'
      case 'other':
        return 'suitcase'
      default:
        return 'template'
    }
  }

  return (
    <WithPaywall
      allowWhileLoading
      features={[Features.procurement_purchase_orders]}
      modules={[AvailableModules.procurement]}
    >
      {() => (
        <ResponsivePage.Body
          variant="wide"
          authorized={!!data?.procurement._policies.purchaseRequestCreate.can}
          loading={loading}
          error={error}
        >
          <InfiniteLoader controls={controls}>
            <Grid variant="medium">
              {requestTypes.map((requestType) => (
                <Card
                  key={requestType.identifier}
                  to={{
                    pathname: navigation.myPurchases.newRequest.fullPath(),
                    search: createSearchParams({ modal: 'new' }).toString(),
                  }}
                  onClick={() => setSelectedType(requestType)}
                >
                  <Card.Eyebrow>
                    <Icon.Large icon={getIcon(requestType.identifier)} color="radicalRed1000" />
                  </Card.Eyebrow>
                  <Card.Title>{requestType.name}</Card.Title>
                  <Card.Description>{requestType.description || ''}</Card.Description>
                </Card>
              ))}
            </Grid>
          </InfiniteLoader>
          {isFormOpen && selectedType && (
            <PurchaseRequestForm
              procurementType={selectedType}
              isOpen={isFormOpen}
              onClose={() => {
                setSelectedType(null)
                setSearchParams((prev) => {
                  prev.delete('modal')
                  return prev
                })
              }}
            />
          )}
          <Outlet />
        </ResponsivePage.Body>
      )}
    </WithPaywall>
  )
}

export default NewRequestPage
